/**
 * Mesures de performance (fps, temps CPU par image, draw calls, triangles,
 * instances) et qualité adaptative : si le framerate baisse durablement, la
 * résolution de rendu et les distances de LOD diminuent, puis remontent.
 * Les mesures sont exposées sur `window.__snowchain.perf()` pour le banc de
 * mesure automatisé (tools/perf).
 */
import type * as THREE from 'three/webgpu';
import { InstancedLodSet } from './instancing';

export interface PerfSample {
  fps: number;
  frameMs: number;
  cpuMs: number;
  cpuMaxMs: number;
  drawCalls: number;
  triangles: number;
  pixelRatio: number;
  lodScale: number;
}

export class PerfMonitor {
  private frames = 0;
  private acc = 0;
  private cpuAcc = 0;
  private cpuMax = 0;
  private windowStart = performance.now();
  last: PerfSample = { fps: 0, frameMs: 0, cpuMs: 0, cpuMaxMs: 0, drawCalls: 0, triangles: 0, pixelRatio: 1, lodScale: 1 };
  /** Historique (1 point par seconde) pour le rapport. */
  readonly history: PerfSample[] = [];
  private lowStreak = 0;
  private highStreak = 0;
  adaptive = true;
  maxPixelRatio: number;

  constructor(private readonly renderer: THREE.WebGPURenderer) {
    this.maxPixelRatio = Math.min(window.devicePixelRatio || 1, 1.75);
    if (new URLSearchParams(location.search).has('noadapt')) this.adaptive = false;
  }

  frame(dtMs: number, cpuMs: number): PerfSample | null {
    this.frames++;
    this.acc += dtMs;
    this.cpuAcc += cpuMs;
    this.cpuMax = Math.max(this.cpuMax, cpuMs);
    const now = performance.now();
    if (now - this.windowStart < 1000) return null;
    const info = this.renderer.info.render as unknown as { drawCalls: number; triangles: number };
    const s: PerfSample = {
      fps: Math.round((this.frames * 1000) / (now - this.windowStart)),
      frameMs: Math.round((this.acc / this.frames) * 100) / 100,
      cpuMs: Math.round((this.cpuAcc / this.frames) * 100) / 100,
      cpuMaxMs: Math.round(this.cpuMax * 100) / 100,
      drawCalls: info.drawCalls,
      triangles: info.triangles,
      pixelRatio: this.renderer.getPixelRatio(),
      lodScale: InstancedLodSet.lodScale,
    };
    this.last = s;
    this.history.push(s);
    if (this.history.length > 600) this.history.shift();
    this.frames = 0;
    this.acc = 0;
    this.cpuAcc = 0;
    this.cpuMax = 0;
    this.windowStart = now;
    if (this.adaptive) this.adapt(s);
    return s;
  }

  private adapt(s: PerfSample): void {
    if (s.fps < 48) {
      this.lowStreak++;
      this.highStreak = 0;
    } else if (s.fps >= 58) {
      this.highStreak++;
      this.lowStreak = 0;
    } else {
      this.lowStreak = 0;
      this.highStreak = 0;
    }
    const pr = this.renderer.getPixelRatio();
    if (this.lowStreak >= 2) {
      this.lowStreak = 0;
      if (pr > 0.8) this.renderer.setPixelRatio(Math.max(0.75, pr - 0.15));
      else InstancedLodSet.lodScale = Math.max(0.55, InstancedLodSet.lodScale * 0.88);
    } else if (this.highStreak >= 5) {
      this.highStreak = 0;
      if (InstancedLodSet.lodScale < 1) InstancedLodSet.lodScale = Math.min(1, InstancedLodSet.lodScale * 1.08);
      else if (pr < this.maxPixelRatio) this.renderer.setPixelRatio(Math.min(this.maxPixelRatio, pr + 0.1));
    }
  }
}
